import { useMemo } from 'react';

import { Metrics } from 'components/insights/constants';
import { formatPercentage } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useProfile } from 'context/profile';

import MetricDetailLayout from '../../shared/slideout/MetricDetailLayout';
import { StatTile } from '../../shared/slideout/statTiles';
import { CHOSE_DIFFERENT_FACILITY, isRealDimensionValue, notSelectedReasonLabel } from '../constants';

import InboundNotSelectedBreakdownChart, {
  NOT_SELECTED_CHART_HEIGHT,
  NotSelectedReasonRow,
} from './InboundNotSelectedBreakdownChart';
import useInboundSelectedFacilityExtremes from './useInboundSelectedFacilityExtremes';

type Props = {
  loading: boolean;
  initialLoading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  data: AnalyticsResponseData;
};

// Metric drill for the Not Selected rate tile: not-selected rate + top reason + least-selected SNF,
// over a ranked bar list of not-selected reasons. Everything here is dimension-independent (the rate
// tile reads the grand-total rollup; the reason breakdown is the same regardless of the table's View
// By), so it gates on initialLoading — under keepPreviousData the values (and the bar count, so the
// chart height) stay put on a switch. The reason breakdown is derived from the main query's
// grouping-0 leaves (the same leaves the breakdown table's sub-rows parse), summed over the entity
// dim. The least-selected SNF rides the shared facility-extremes side query (always by facility).
export default function NotSelectedDetailHeader({ initialLoading, rollups, data }: Props) {
  const overall = rollups.overall.metrics;
  const notSelectedRate = overall.getCurrentDataPoint(Metrics.NOT_SELECTED_RATE, 0);
  const notSelectedCount = overall.getCurrentDataPoint(Metrics.NOT_SELECTED_COUNT, 0) ?? 0;
  // "Closed" = every building that reached a recorded outcome (selected + not_selected), read from
  // the closed_count metric.
  const referralsClosed = overall.getCurrentDataPoint(Metrics.CLOSED_COUNT, 0) ?? 0;

  const { least: leastFacility, loading: leastLoading } = useInboundSelectedFacilityExtremes();

  // The synthetic no-reason bucket names the client, and the name is the viewer's own:
  // this header aggregates across referrals, and a viewer only sees their own client's.
  const { profile } = useProfile();
  const clientName = profile.actingClient?.name;

  const { reasonRows, topReason, total } = useMemo(() => {
    const reasons = data.dimensions.getCurrentDimensionValuesByIndex(1);
    const counts = data.metrics.getCurrentSeries(Metrics.NOT_SELECTED_COUNT);

    // A NULL/'none' reason is the synthetic bucket: a building on a referral one of this client's own
    // buildings won. Mostly that means it lost the competition; see CHOSE_DIFFERENT_FACILITY for the
    // one case the label is looser than the bucket (a building that denied an otherwise-won referral
    // lands here too, and nothing at this level can separate them). Keyed to one bucket, kept.
    const byKey = new Map<string, number>();
    reasons.forEach((slug, i) => {
      const count = counts[i] ?? 0;
      if (count <= 0) return;
      const key = isRealDimensionValue(slug) ? slug : CHOSE_DIFFERENT_FACILITY;
      byKey.set(key, (byKey.get(key) ?? 0) + count);
    });

    const rows: NotSelectedReasonRow[] = [...byKey.entries()]
      .map(([key, count]) => ({ label: notSelectedReasonLabel(key, clientName), count }))
      .sort((a, b) => b.count - a.count);

    return { reasonRows: rows, topReason: rows[0] ?? null, total: rows.reduce((sum, r) => sum + r.count, 0) };
  }, [data, clientName]);

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label='Avg. Not Selected Rate'
            loading={initialLoading}
            inlineAddendum
            primary={formatPercentage(notSelectedRate)}
            addendum={notSelectedRate != null ? `${notSelectedCount} of ${referralsClosed} total closed` : undefined}
          />
          <StatTile label='Top Not Selected Reason' loading={initialLoading} primary={topReason?.label ?? '—'} />
          <StatTile
            label='Least Selected SNF'
            tooltipText='Based on Not Selected Rate'
            loading={leastLoading}
            primary={leastFacility?.name ?? '—'}
          />
        </>
      }
      chartTitle='Not Selected Reason'
      chartLoading={initialLoading}
      chartHeight={NOT_SELECTED_CHART_HEIGHT}
      isEmpty={reasonRows.length === 0}
      emptyMessage='No not-selected reasons to show for this time period.'>
      <InboundNotSelectedBreakdownChart reasonRows={reasonRows} total={total} />
    </MetricDetailLayout>
  );
}
